"use client";
import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Checkbox } from '../Checkbox/Checkbox.web.js';
import { Th } from '../Table/elements/Th.web.js';
import { Td } from '../Table/elements/Td.web.js';
import { wrapper, table, tr } from '../Table/styles.js';
import { defaultSortComparator } from '../utils/helpers/array.js';
import { ThemeProvider } from 'styled-components';
import { useTrackTableScroll } from '../Table/utils.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { selectableWrapper } from './styles.js';
import { Tr } from '../Table/elements/Tr.web.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { spacing } from '../constants/spacing.js';
import { callOptional } from '../utils/helpers/function.js';
import { IconChevronRight } from '../icons/ui_navigation/IconChevronRight.web.js';
import { IconChevronDown } from '../icons/ui_navigation/IconChevronDown.web.js';

var _excluded = ["columns", "cellProps", "groupHeader", "size", "rowStyle", "columnStyle", "rowGroups", "getRowKey", "getGroupKey", "getRowProps", "onRowClick", "onGroupClick", "selectedRows", "onSelect", "sortBy", "onSortByChange", "sortOrder", "onSortOrderChange", "color", "selectAllRowsAriaLabel", "selectRowGroupAriaLabel", "selectRowAriaLabel", "tableProps", "stickyHeader", "selectable", "collapsible", "collapsedGroups", "collapseRowsAriaLabel", "onCollapsedGroupsChange", "variant"];
var DEFAULT_SELECT_ROW_ARIA_LABEL = 'Select row checkbox';
var DEFAULT_SELECT_ROW_GROUP_ARIA_LABEL = 'Select row group checkbox';
var empty = [];
var TreeList = function TreeList(_ref) {
  var columns = _ref.columns,
    cellProps = _ref.cellProps,
    groupHeader = _ref.groupHeader,
    sizeProp = _ref.size,
    rowStyle = _ref.rowStyle,
    _ref$columnStyle = _ref.columnStyle,
    columnStyle = _ref$columnStyle === undefined ? 'clear' : _ref$columnStyle,
    _ref$rowGroups = _ref.rowGroups,
    rowGroups = _ref$rowGroups === undefined ? empty : _ref$rowGroups,
    getRowKey = _ref.getRowKey,
    getGroupKey = _ref.getGroupKey,
    getRowProps = _ref.getRowProps,
    onRowClick = _ref.onRowClick,
    onGroupClick = _ref.onGroupClick,
    selectedRowsProp = _ref.selectedRows,
    onSelectProp = _ref.onSelect,
    sortByProp = _ref.sortBy,
    onSortByChangeProp = _ref.onSortByChange,
    sortOrderProp = _ref.sortOrder,
    onSortOrderChangeProp = _ref.onSortOrderChange,
    colorProp = _ref.color,
    _ref$selectAllRowsAri = _ref.selectAllRowsAriaLabel,
    selectAllRowsAriaLabel = _ref$selectAllRowsAri === undefined ? 'Select all rows checkbox' : _ref$selectAllRowsAri,
    selectRowGroupAriaLabelProp = _ref.selectRowGroupAriaLabel,
    selectRowAriaLabelProp = _ref.selectRowAriaLabel,
    tableProps = _ref.tableProps,
    stickyHeader = _ref.stickyHeader,
    _ref$selectable = _ref.selectable,
    selectable = _ref$selectable === undefined ? false : _ref$selectable,
    collapsible = _ref.collapsible,
    collapsedGroupsProp = _ref.collapsedGroups,
    _ref$collapseRowsAria = _ref.collapseRowsAriaLabel,
    collapseRowsAriaLabel = _ref$collapseRowsAria === undefined ? 'Toggle visibility' : _ref$collapseRowsAria,
    onCollapsedGroupsChange = _ref.onCollapsedGroupsChange,
    variantProp = _ref.variant,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlled = useSmartControlled(empty, sortByProp, onSortByChangeProp),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    sortBy = _useSmartControlled2[0],
    setSortBy = _useSmartControlled2[1];
  var _useSmartControlled3 = useSmartControlled(empty, sortOrderProp, onSortOrderChangeProp),
    _useSmartControlled4 = _slicedToArray(_useSmartControlled3, 2),
    sortOrder = _useSmartControlled4[0],
    setSortOrder = _useSmartControlled4[1];
  var _useSmartControlled5 = useSmartControlled(empty, selectedRowsProp, onSelectProp),
    _useSmartControlled6 = _slicedToArray(_useSmartControlled5, 2),
    selectedRows = _useSmartControlled6[0],
    setSelectedRows = _useSmartControlled6[1];
  var selectedSet = React.useMemo(function () {
    return new Set(selectedRows);
  }, [selectedRows]);
  var _useSmartControlled7 = useSmartControlled(empty, collapsedGroupsProp, onCollapsedGroupsChange),
    _useSmartControlled8 = _slicedToArray(_useSmartControlled7, 2),
    collapsedGroups = _useSmartControlled8[0],
    setCollapsedGroups = _useSmartControlled8[1];
  var collapsed = React.useMemo(function () {
    return new Set(collapsedGroups);
  }, [collapsedGroups]);
  var wrapperRef = React.useRef(null);
  var _useTrackTableScroll = useTrackTableScroll(wrapperRef),
    scrolling = _useTrackTableScroll.scrolling,
    scrollEndTop = _useTrackTableScroll.scrollEndTop,
    handleScrolling = _useTrackTableScroll.handleScrolling;
  var handleRowSort = function handleRowSort(e, columnKey) {
    var index = sortBy.indexOf(columnKey);
    var currentOrder = sortOrder[index];
    setSortBy([columnKey]);
    setSortOrder(currentOrder === 'asc' ? ['desc'] : ['asc']);
  };
  var handleRowSelect = function handleRowSelect(rowKey) {
    return function () {
      if (selectedSet.has(rowKey)) {
        setSelectedRows(selectedRows.filter(function (selectedRow) {
          return selectedRow !== rowKey;
        }));
      } else {
        setSelectedRows(selectedRows.concat([rowKey]));
      }
    };
  };
  var handleGroupSelect = function handleGroupSelect(rowKeys) {
    return function () {
      if (rowKeys.some(function (rowKey) {
        return selectedSet.has(rowKey);
      })) {
        // FIXME m*n algo
        setSelectedRows(selectedRows.filter(function (selectedRow) {
          return !rowKeys.includes(selectedRow);
        }));
      } else {
        setSelectedRows(selectedRows.concat(rowKeys));
      }
    };
  };
  var handleAllSelect = function handleAllSelect() {
    var rows = rowGroups.map(function (group) {
      return group.children;
    }).flat();
    if (rows.length === selectedRows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows.map(getRowKey));
    }
  };
  var handleRowClick = function handleRowClick(value) {
    return function () {
      callOptional(onRowClick)(value);
    };
  };
  var handleGroupClick = function handleGroupClick(value) {
    return function () {
      callOptional(onGroupClick)(value);
    };
  };
  var noPropagate = function noPropagate(e) {
    e.stopPropagation();
  };
  var elementalTheme = useTheme();
  var tableTheme = elementalTheme.table.web;
  var variant = variantProp || elementalTheme.variant;

  // istanbul ignore next
  var size = sizeProp !== undefined ? sizeProp : variant === 'condensed' ? 'condensed' : 'medium';
  var color = colorProp || tableTheme.color;
  var rowGroupsSelectable = selectable === true || selectable === 'rowGroups';
  var rowsSelectable = selectable === true || selectable === 'rows';
  var selectionEnabled = rowGroupsSelectable || rowsSelectable;
  var selectRowAriaLabel = function selectRowAriaLabel(row) {
    if (!selectRowAriaLabelProp) {
      var firstColumn = columns[0];
      return /* istanbul ignore next */firstColumn ? "".concat(DEFAULT_SELECT_ROW_ARIA_LABEL, " ").concat(firstColumn.getCellData(row, firstColumn)) : DEFAULT_SELECT_ROW_ARIA_LABEL;
    }
    return typeof selectRowAriaLabelProp === 'function' ? selectRowAriaLabelProp(row) : selectRowAriaLabelProp;
  };
  var selectRowGroupAriaLabel = function selectRowGroupAriaLabel(rowGroup) {
    if (!selectRowGroupAriaLabelProp) {
      return "".concat(DEFAULT_SELECT_ROW_GROUP_ARIA_LABEL, " ").concat(rowGroup.name);
    }
    return typeof selectRowGroupAriaLabelProp === 'function' ? selectRowGroupAriaLabelProp(rowGroup) : selectRowGroupAriaLabelProp;
  };
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: elementalTheme
  }, /*#__PURE__*/React.createElement(wrapper, _extends({
    "data-testid": "elemental-TreeList",
    $color: color,
    $stickyHeader: stickyHeader,
    ref: wrapperRef,
    onScroll: handleScrolling
  }, props), /*#__PURE__*/React.createElement(table, _extends({
    "data-vertical-scroll":
    // istanbul ignore next
    stickyHeader && !scrollEndTop && scrolling,
    $rowStyle: rowStyle,
    $columnStyle: columnStyle,
    $selectable: selectionEnabled,
    $stickyHeader: stickyHeader,
    $clickable: typeof onRowClick === 'function',
    $color: color,
    $variant: variant,
    width: "100%"
  }, tableProps), /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement(tr, {
    $selectable: selectionEnabled,
    "data-testid": "elemental-TreeList-head-tr",
    $head: true,
    $color: color
  }, /*#__PURE__*/React.createElement(Th, {
    size: size,
    columnKey: "__elemental-selectable",
    color: color,
    stickyHeader: stickyHeader,
    paddingLeft: selectionEnabled ? spacing.xxs : spacing.xxxxs,
    paddingRight: spacing.xxs
  }, selectable && /*#__PURE__*/React.createElement(Checkbox, {
    name: "select-all",
    checked: rowGroups.map(function (group) {
      return group.children;
    }).flat().length === selectedRows.length,
    onChange: handleAllSelect,
    inputProps: {
      'aria-label': selectAllRowsAriaLabel
    },
    value: "on",
    color: color,
    variant: variant
  })), columns.map(function (column, i) {
    var index = sortBy.indexOf(column.key);
    var thisOrder = sortOrder[index];
    return /*#__PURE__*/React.createElement(Th, {
      key: column.key,
      columnKey: column.key,
      onSort: handleRowSort,
      size: size,
      sortByThis: index >= 0,
      sortOrder: thisOrder,
      color: color,
      stickyHeader: stickyHeader,
      paddingLeft: i === 0 ? 0 : undefined,
      sortable: column.sortable
    }, column.name);
  }))), /*#__PURE__*/React.createElement("tbody", null, rowGroups.map(function (rowGroup) {
    var groupKey = getGroupKey(rowGroup);
    var groupSelectionCount = rowGroup.children.map(getRowKey).map(function (key) {
      return selectedSet.has(key);
    }).reduce(function (acc, n) {
      return n ? acc + 1 : acc;
    }, 0);
    var sortedRows = function sortedRows() {
      var sortFn = function sortFn(column, order) {
        return function (a, b) {
          var valueA = column.getCellData(a, column);
          var valueB = column.getCellData(b, column);
          var comparator = column.sortComparator ? column.sortComparator : defaultSortComparator;
          return comparator(valueA, valueB) * (order === 'asc' ? 1 : -1);
        };
      };
      var rows = rowGroup.children;

      // Create { column, order } pairs from sortBy, sortOrder arrays
      var sortParams = sortBy.map(function (columnKey, index) {
        return {
          // FIXME
          // eslint-disable-next-line
          column: columns.find(function (column) {
            return column.key === columnKey;
          }),
          order: sortOrder[index]
        };
      });

      // Reduce over { column, order } pairs (starting from the right), calling
      // .sort() on a copy of rows at each iteration
      return sortParams.reduceRight(function (arr, _ref2) {
        var column = _ref2.column,
          order = _ref2.order;
        return arr.sort(sortFn(column, order));
      }, _toConsumableArray(rows));
    };
    var rowData = groupHeader.getCellData(rowGroup, groupHeader);
    var highlightColor = callOptional(groupHeader.cellHighlightColor)(rowData, rowGroup);
    var cellStyles;
    if (typeof groupHeader.cellContainerStyle === 'function') {
      cellStyles = callOptional(groupHeader.cellContainerStyle)(rowData, rowGroup);
    } else {
      cellStyles = groupHeader.cellContainerStyle;
    }
    var groupRowStyles;
    if (typeof getRowProps === 'function') {
      groupRowStyles = callOptional(getRowProps)(rowGroup, null);
    }
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: groupKey
    }, /*#__PURE__*/React.createElement(Tr, _extends({
      "data-testid": "elemental-TreeList-Group-".concat(groupKey),
      onClick: handleGroupClick(rowGroup),
      "aria-selected": groupSelectionCount > 0,
      selected: groupSelectionCount > 0,
      selectable: rowGroupsSelectable,
      clickable: typeof onGroupClick === 'function',
      color: color
    }, groupRowStyles), /*#__PURE__*/React.createElement(Td, _extends({
      "data-testid": "elemental-TreeList-Row-".concat(groupKey, "-header"),
      size: size,
      colSpan: columns.length + 1,
      highlightColor: highlightColor,
      paddingLeft: spacing.xxs
    }, cellProps, cellStyles), /*#__PURE__*/React.createElement(selectableWrapper, {
      onClick: selectable ? noPropagate : undefined
    }, collapsible && /*#__PURE__*/React.createElement(IconButton, {
      status: "plain",
      size: 24,
      "data-testid": "elemental-TreeList-Group-".concat(groupKey, "-collapse"),
      disabled: rowGroup.children.length < 1,
      "aria-label": collapseRowsAriaLabel,
      mr: spacing.xxs,
      color: groupSelectionCount > 0 ? tableTheme.background : tableTheme.textColor,
      onClick: function onClick() {
        if (collapsed.has(groupKey)) {
          collapsed["delete"](groupKey);
        } else {
          collapsed.add(groupKey);
        }
        setCollapsedGroups(_toConsumableArray(collapsed.values()));
      },
      icon: collapsed.has(groupKey) ? /*#__PURE__*/React.createElement(IconChevronRight, null) : /*#__PURE__*/React.createElement(IconChevronDown, null)
    }), selectionEnabled && /*#__PURE__*/React.createElement(Checkbox, {
      checked: groupSelectionCount === rowGroup.children.length,
      transparent: groupSelectionCount > 0,
      color: groupSelectionCount > 0 ? tableTheme.background : color,
      visibility: rowGroupsSelectable ? undefined : 'hidden',
      disabled: !rowGroupsSelectable,
      name: "select",
      readOnly: rowGroup.children.length === 0,
      onChange: handleGroupSelect(rowGroup.children.map(getRowKey)),
      inputProps: {
        'aria-label': selectRowGroupAriaLabel(rowGroup)
      }
      // HOX! if ever modifying this make sure both 1 and '1' won't conflict!
      ,
      value: String(groupKey),
      marginRight: spacing.xxs
    }), groupHeader.renderCell ? groupHeader.renderCell(rowData, rowGroup) : rowData))), sortedRows().map(function (row) {
      var rowKey = getRowKey(row);
      var rowSelected = selectedSet.has(rowKey);
      var rowStyles;
      if (typeof getRowProps === 'function') {
        rowStyles = callOptional(getRowProps)(rowGroup, row);
      }
      return /*#__PURE__*/React.createElement(Tr, _extends({
        "data-testid": "elemental-TreeList-Row-".concat(rowKey),
        onClick: handleRowClick(row),
        key: rowKey,
        selected: rowSelected,
        selectable: rowsSelectable,
        clickable: typeof onRowClick === 'function',
        color: color,
        collapsed: collapsed.has(groupKey),
        "aria-hidden": collapsed.has(groupKey)
      }, rowStyles), columns.map(function (column, i) {
        var rowData = column.getCellData(row, column);
        var numeric = typeof rowData === 'number' && isFinite(rowData) && !column.renderCell;
        var highlightColor = callOptional(column.cellHighlightColor)(rowData, row);
        var cellStyles;
        if (typeof column.cellContainerStyle === 'function') {
          cellStyles = callOptional(column.cellContainerStyle)(rowData, row);
        } else {
          cellStyles = column.cellContainerStyle;
        }
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: column.key
        }, /*#__PURE__*/React.createElement(Td, _extends({
          "data-testid": "elemental-TreeList-Row-".concat(rowKey, "-").concat(column.key),
          numeric: numeric,
          size: size,
          paddingLeft: i === 0 ? spacing.l : undefined,
          highlightColor: highlightColor,
          colSpan: i === 0 ? 2 : undefined
        }, cellProps, cellStyles), i == 0 ? /*#__PURE__*/React.createElement(selectableWrapper, {
          onClick: noPropagate
        }, selectionEnabled && /*#__PURE__*/React.createElement(Checkbox, {
          checked: rowSelected,
          transparent: rowSelected,
          visibility: rowsSelectable ? undefined : 'hidden',
          disabled: !rowsSelectable,
          name: "select",
          onChange: handleRowSelect(rowKey),
          inputProps: {
            'aria-label': selectRowAriaLabel(row)
          },
          value: "".concat(groupKey, "-").concat(rowKey),
          marginRight: spacing.xxs,
          color: rowSelected ? tableTheme.background : color
        }), column.renderCell ? column.renderCell(rowData, row) : rowData) : column.renderCell ? column.renderCell(rowData, row) : rowData));
      }));
    }));
  })))));
};

export { TreeList };
